import {
  init,
  setUser,
  getCurrentScope,
  extraErrorDataIntegration,
  httpClientIntegration,
  reactRouterV5BrowserTracingIntegration,
  browserProfilingIntegration,
} from '@sentry/react';

import { STORAGE_KEYS } from 'constants/enums';
import { getGrowthBookFeatureValue } from 'growthbook';

import { history } from './configureStore';
import { isRNWebview, RELEASE_VERSION, SENTRY_DSN } from './constants/constants';

const isStatus4xx = (hint: any) => {
  const status = hint?.originalException?.status;
  return typeof status === 'string' && /^4\d{2}$/.test(status);
};

const ignoreExceptionToSentry = (exception: string) => {
  const ignoreErrorPatterns = [
    'Request has been terminated',
    'internal_server_error',
    'You provided an invalid object where a stream was expected',
    'Non-Error exception captured with keys: currentTarget, isTrusted',
    'Load failed',
    'Non-Error exception captured with keys: code, message',
    'Loading chunk',
    'Loading CSS chunk',
    "Cannot read properties of null (reading 'style')",
    "Failed to execute 'scrollTo' on 'Window'",
    'ResizeObserver loop completed with undelivered notifications.',
    'window.onMetaWidget is not a constructor',
    'Failed to fetch',
    'HTTP error',
    'o is not a function',
    'Unexpected token "<"',
    'Navigation cancelled',
    'AbortError',
    'The operation was aborted',
    'Network request failed',
    'Request aborted',
    'Request failed with status code 499', // Client Closed Request
    'Request timed out',
    'User aborted',
    'Error: Request has been timed out',
    'Connection to Indexed Database server lost. Refresh the page to try again', // ref - https://bugs.webkit.org/show_bug.cgi?id=277615
  ];
  return ignoreErrorPatterns.filter(err => exception.indexOf(err) !== -1).length > 0;
};

const initSentry = () => {
  if (SENTRY_DSN) {
    init({
      dsn: SENTRY_DSN,
      integrations: [
        reactRouterV5BrowserTracingIntegration({ history }),
        extraErrorDataIntegration(),
        httpClientIntegration(),
        browserProfilingIntegration(),
      ],
      release: RELEASE_VERSION,
      beforeSend(event, hint) {
        // Check if it's a navigation-related error
        const error = hint?.originalException;
        if (error instanceof Error) {
          // Filter out aborted requests and navigation errors
          if (
            error.name === 'AbortError' ||
            error.message.includes('canceled') ||
            error.message.includes('aborted')
          ) {
            return null;
          }
        }

        // Check for 4xx errors and ignored patterns
        return isStatus4xx(hint) ||
          ignoreExceptionToSentry(hint?.originalException?.toString() || '')
          ? null
          : event;
      },
      replaysSessionSampleRate: 0,
      replaysOnErrorSampleRate: 0,
      tracesSampler: () => {
        const sampleRate = getGrowthBookFeatureValue('sentry_trace_sample_rate');
        return sampleRate;
      },
      profilesSampleRate: 0.5,
      normalizeDepth: 5,
      sendDefaultPii: true,
      environment: process.env.REACT_APP_ENV,
      enabled:
        process.env.REACT_APP_ENV === 'production' ||
        process.env.REACT_APP_ENV === 'beta',
    });

    const persistRoot = localStorage.getItem(STORAGE_KEYS.PERSIST_ROOT) || '{}';
    const localUserObjectStr = JSON.parse(persistRoot)?.user || '{}';

    const localUserObject = JSON.parse(localUserObjectStr);

    const userContext = localUserObject?.id
      ? {
          id: localUserObject.id,
          username: localUserObject.userName,
          email: localUserObject.email,
          source: isRNWebview() ? 'ReactNative' : 'WebApp',
        }
      : {
          source: isRNWebview() ? 'ReactNative' : 'WebApp',
          environment: process.env.REACT_APP_ENV,
        };
    setUser(userContext);
    // Set isUserLoggedin tag to filter and categorize errors by authentication state in Sentry dashboard
    getCurrentScope().setTag('isUserLoggedin', localUserObject?.id ? 'true' : 'false');
  }
};

// Initialize Sentry immediately
initSentry();
